import React, { useState, useRef, useMemo } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { QuestionDetail } from '../services/api';

interface DetailedScoreCardProps {
    questions: QuestionDetail[];
    candidateName: string;
    niesbudRegNo: string;
    onClose: () => void;
}

// ✅ Grouped question type — add ShadowQno
interface GroupedQuestion {
    QuestionNumber: string;   // ✅ actual question number (Q5, Q17, Q27)
    ShadowQno: string;        // ✅ shadow qno (agar chahiye)
    Question: string;
    CorrectAnswer: string;
    GivenAnswer: string;
    Result: string;
    options: {
        Code: string;
        Description: string;
        isCorrect: boolean;
        isGiven: boolean;
    }[];
}

const DetailedScoreCard: React.FC<DetailedScoreCardProps> = ({
    questions,
    candidateName,
    niesbudRegNo,
    onClose,
}) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    // ✅ Group by QuestionNumber (actual question number)
    const groupedQuestions: GroupedQuestion[] = useMemo(() => {
        const map = new Map<string, GroupedQuestion>();

        questions.forEach((q) => {
            // ✅ IMPORTANT: group by QuestionNumber (not ShadowQno)
            const key = q.QuestionNumber;

            if (!map.has(key)) {
                map.set(key, {
                    QuestionNumber: q.QuestionNumber,
                    ShadowQno: q.ShadowQno,        // ✅ store shadow
                    Question: q.Question,
                    CorrectAnswer: q.CorrectAnswer,
                    GivenAnswer: q.GivenAnswer,
                    Result: q.Result,
                    options: [],
                });
            }

            const group = map.get(key)!;

            // Add this row as an option
            group.options.push({
                Code: q.Code,
                Description: q.Description,
                isCorrect: q.Code === q.CorrectAnswer,
                isGiven: q.Code === q.GivenAnswer,
            });

            // ✅ fill master fields if empty
            if (!group.CorrectAnswer && q.CorrectAnswer) {
                group.CorrectAnswer = q.CorrectAnswer;
            }
            if (!group.GivenAnswer && q.GivenAnswer) {
                group.GivenAnswer = q.GivenAnswer;
            }
            if (!group.Result && q.Result) {
                group.Result = q.Result;
            }
            if (!group.Question && q.Question) {
                group.Question = q.Question;
            }
            if (!group.ShadowQno && q.ShadowQno) {
                group.ShadowQno = q.ShadowQno;
            }
        });

        // ✅ Sort options by Code (1, 2, 3, 4)
        map.forEach((group) => {
            group.options.sort((a, b) => Number(a.Code) - Number(b.Code));
        });

        return Array.from(map.values());
    }, [questions]);

    const downloadPDF = async () => {
        if (!contentRef.current) return;
        setIsDownloading(true);

        try {
            // ✅ Get actual rendered width
            const element = contentRef.current;
            const rect = element.getBoundingClientRect();
            const contentWidth = Math.max(rect.width, 800); // minimum 800px

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                // ✅ Force proper dimensions
                width: contentWidth,
                windowWidth: contentWidth,
                height: element.scrollHeight,
                windowHeight: element.scrollHeight,
                scrollX: 0,
                scrollY: 0,
                onclone: (clonedDoc, clonedElement) => {
                    // ✅ CRITICAL: Fix cloned element's width and position
                    const htmlEl = clonedElement as HTMLElement;
                    htmlEl.style.width = contentWidth + 'px';
                    htmlEl.style.maxWidth = contentWidth + 'px';
                    htmlEl.style.minWidth = contentWidth + 'px';
                    htmlEl.style.position = 'static';
                    htmlEl.style.margin = '0';
                    htmlEl.style.padding = '32px';
                    htmlEl.style.backgroundColor = '#ffffff';
                    htmlEl.style.overflow = 'visible';

                    // ✅ Fix parent containers (modal + backdrop)
                    let parent = htmlEl.parentElement;
                    while (parent && parent !== clonedDoc.body) {
                        parent.style.position = 'static';
                        parent.style.overflow = 'visible';
                        parent.style.width = 'auto';
                        parent.style.maxWidth = 'none';
                        parent.style.transform = 'none';
                        parent.style.display = 'block';
                        parent = parent.parentElement;
                    }

                    // ✅ Fix Tailwind v4 oklch colors
                    const style = clonedDoc.createElement('style');
                    style.textContent = `
                    * { 
                        --tw-bg-opacity: 1 !important; 
                        box-sizing: border-box !important;
                    }
                `;
                    clonedDoc.head.appendChild(style);
                },
            });

            console.log('Canvas:', canvas.width, canvas.height);

            if (canvas.width === 0 || canvas.height === 0) {
                throw new Error('Canvas is empty');
            }

            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = pdfWidth / imgWidth; // ✅ fit full width
            const scaledHeight = imgHeight * ratio;

            // ✅ Multi-page logic (proper)
            let heightLeft = scaledHeight;
            let position = 0;

            pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, scaledHeight);
            heightLeft -= pdfHeight;

            while (heightLeft > 0) {
                position = heightLeft - scaledHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, scaledHeight);
                heightLeft -= pdfHeight;
            }

            pdf.save(`ScoreCard_${niesbudRegNo}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to download PDF: ' + (error as Error).message);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
            <div className="min-h-screen p-4 flex items-start justify-center">
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl">
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center rounded-t-lg z-10">
                        <h2 className="text-xl font-bold text-slate-800">
                            Detailed Score Card
                        </h2>
                        <div className="flex gap-2">
                            <button
                                onClick={downloadPDF}
                                disabled={isDownloading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="animate-spin">⏳</span>
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <span>📥</span>
                                        Download PDF
                                    </>
                                )}
                            </button>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                            >
                                ✕ Close
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div ref={contentRef} className="p-8 bg-white">
                        {/* Candidate Info */}
                        <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
                            <h3 className="font-bold text-lg mb-2">
                                {candidateName}
                            </h3>
                            <p className="text-sm text-slate-600">
                                Reg. No: {niesbudRegNo}
                            </p>
                        </div>

                        {/* ✅ Grouped Questions */}
                        <div className="space-y-6">
                            {groupedQuestions.map((question, index) => {
                                const isCorrect =
                                    question.Result === 'Correct' ||
                                    (question.GivenAnswer &&
                                        question.GivenAnswer ===
                                        question.CorrectAnswer);

                                return (
                                    <div
                                        key={index}
                                        className="border rounded-lg p-4 bg-white"
                                    >
                                        {/* ✅ Question header — show QuestionNumber (Q5, Q17, Q27...) */}
                                        <div className="flex items-start gap-3 mb-3">
                                            <span className="bg-blue-100 text-blue-800 text-sm font-bold px-3 py-1 rounded-full whitespace-nowrap">
                                                {question.ShadowQno}
                                            </span>
                                            <p className="text-slate-800 font-medium flex-1">
                                                {question.Question}
                                            </p>
                                        </div>

                                        {/* ✅ Options with Code (1, 2, 3, 4) */}
                                        <div className="ml-4 space-y-2">
                                            {question.options.map(
                                                (option, optIndex) => {
                                                    const status = option.isCorrect
                                                        ? option.isGiven
                                                            ? 'correct-given'
                                                            : 'correct-not-given'
                                                        : option.isGiven
                                                            ? 'wrong-given'
                                                            : 'neutral';

                                                    return (
                                                        <div
                                                            key={optIndex}
                                                            className={`flex items-start gap-2 p-2 rounded ${status ===
                                                                    'correct-given'
                                                                    ? 'bg-green-50 border border-green-300'
                                                                    : status ===
                                                                        'correct-not-given'
                                                                        ? 'bg-green-50 border border-green-300'
                                                                        : status ===
                                                                            'wrong-given'
                                                                            ? 'bg-red-50 border border-red-300'
                                                                            : 'bg-slate-50'
                                                                }`}
                                                        >
                                                            {/* ✅ Option Code (1, 2, 3, 4) */}
                                                            <span className="font-medium text-slate-600 min-w-[24px]">
                                                                {option.Code}.
                                                            </span>
                                                            <span className="flex-1 text-slate-700">
                                                                {
                                                                    option.Description
                                                                }
                                                            </span>

                                                            {status ===
                                                                'correct-given' && (
                                                                    <span className="text-green-600 font-bold whitespace-nowrap">
                                                                        ✓ Correct
                                                                    </span>
                                                                )}
                                                            {status ===
                                                                'correct-not-given' && (
                                                                    <span className="text-green-600 text-sm whitespace-nowrap">
                                                                        (Correct
                                                                        Answer)
                                                                    </span>
                                                                )}
                                                            {status ===
                                                                'wrong-given' && (
                                                                    <span className="text-red-600 font-bold whitespace-nowrap">
                                                                        ✗ Your
                                                                        Answer
                                                                    </span>
                                                                )}
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>

                                        {/* Result Badge */}
                                        <div className="mt-3 flex justify-end">
                                            <span
                                                className={`px-3 py-1 rounded-full text-sm font-medium ${isCorrect
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                    }`}
                                            >
                                                {isCorrect
                                                    ? '✓ Correct'
                                                    : '✗ Incorrect'}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="mt-8 pt-4 border-t text-center text-xs text-slate-400">
                            <p>
                                This is a computer-generated score card. No
                                signature required.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailedScoreCard;